import React, { useEffect, useState, useRef, useCallback } from "react";
import InfiniteScroller from "../InfiniteScroller/InfiniteScroller";
import EventCard from "../EventCard/EventCard";
import Loader from "../icons/Loader";
import PullToActionWrapper from "../PullToActionWrapper/PullToActionWrapper";
import { useWatchList } from "../../context/WatchListContext/WatchListProvider";
import { useEventDetails } from "../../context/EventDetailsProvider/EventDetailsProvider";
import { useUser } from "../../context/UserContext/UserProvider";
import ProfilePicture from "../icons/ProfilePicture";
import { BottomSheetDateFilter } from "../Map/Map";
import Input from "../Input/Input";
import { LuEye } from "react-icons/lu";
import { FiSearch } from "react-icons/fi";
import { FaRegEye, FaCheck } from "react-icons/fa6";
import { IoFilter } from "react-icons/io5";
import { IoMdClose } from "react-icons/io";
import { useAccountDetails } from "../../context/AccountDetailsContext/AccountDetailsProvider";
import axiosInstance from "../../api/axiosInstance.js";
import { backendUrl } from "../../api/constants";
import { useMediaQuery } from "../../util/useMediaQuery";
import EventDetails from "../EventDetails/EventDetails";
import AccountDetails from "../AccountDetails/AccountDetails";
import "./Watchlist.css";
import { useUIContext } from "../../context/UIContext/UIContext.jsx";

const AccountBubble = ({
  account,
  size = "4rem",
  className = "",
  isActive,
  onFilter,
  filterEnabled = false,
  showCheckIcon = false,
}) => {
  const { selectAccount } = useAccountDetails();
  const [accountDetails, setAccountDetails] = useState(null);
  const [pressStartTime, setPressStartTime] = useState(null);
  const [pressTimer, setPressTimer] = useState(null);
  const [actionFired, setActionFired] = useState(false);
  const [isPressed, setIsPressed] = useState(false);
  const [dragged, setDragged] = useState(false);
  const initialPos = useRef({ x: 0, y: 0 });
  const DRAG_THRESHOLD = 5; // pixels

  const fetchAccountDetails = async (accountId) => {
    try {
      const { data } = await axiosInstance.get(
        `${backendUrl}/user/${accountId}`
      );
      console.log("fetched account details :", data);
      setAccountDetails(data.account);
    } catch (error) {
      console.error(error);
      // Mark as error so it won't be rendered
      setAccountDetails({ error: true });
    }
  };

  useEffect(() => {
    if (account && account?._id) {
      fetchAccountDetails(account._id);
    } else {
      // Mark as error so it won't be rendered
      setAccountDetails({ error: true });
    }
  }, [account]);

  const handlePress = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setPressStartTime(Date.now());
    setActionFired(false);
    setIsPressed(true);
    setDragged(false);

    // Record initial pointer/touch position.
    const clientX =
      e.touches && e.touches[0] ? e.touches[0].clientX : e.clientX;
    const clientY =
      e.touches && e.touches[0] ? e.touches[0].clientY : e.clientY;
    initialPos.current = { x: clientX, y: clientY };

    if (filterEnabled) {
      const timer = setTimeout(() => {
        // If not dragged, trigger selectAccount after long press
        if (!dragged) {
          setActionFired(true);
          selectAccount(account);
        }
      }, 600);
      setPressTimer(timer);
    }
    // For non-filter mode, we wait until release to check if it was a drag.
  };

  const handleMove = (e) => {
    let clientX, clientY;
    if (e.touches && e.touches.length > 0) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }
    if (
      !dragged &&
      (Math.abs(clientX - initialPos.current.x) > DRAG_THRESHOLD ||
        Math.abs(clientY - initialPos.current.y) > DRAG_THRESHOLD)
    ) {
      setDragged(true);
    }
  };

  const handleRelease = (e) => {
    e.preventDefault();
    e.stopPropagation();

    // Avoid processing if mouse/touch isn't pressed.
    if (!isPressed) {
      return;
    }

    if (filterEnabled) {
      if (pressTimer) {
        clearTimeout(pressTimer);
        setPressTimer(null);
      }
      if (!actionFired && !dragged) {
        onFilter && onFilter(account._id);
      }
    } else {
      if (!dragged) {
        selectAccount(account);
      }
    }
    setPressStartTime(null);
    setIsPressed(false);
  };

  // If account details is not valid, don't render anything
  if (!accountDetails || !accountDetails?._id || accountDetails?.error) {
    return null;
  }

  return (
    <div
      onMouseDown={handlePress}
      onMouseMove={handleMove}
      onMouseUp={handleRelease}
      onMouseLeave={handleRelease}
      onTouchStart={handlePress}
      onTouchMove={handleMove}
      onTouchEnd={handleRelease}
      onTouchCancel={handleRelease}
      className={`flex flex-col w-fit items-center justify-center cursor-pointer ${
        isActive ? "opacity-100" : "opacity-60"
      } ${className}`}
    >
      <div
        style={{ width: size, height: size }}
        className={`
          relative mb-kit-8 transform transition-transform duration-200 
          ${showCheckIcon && filterEnabled ? "rounded-kit-full border-[2px] border-[--inyc-primary-text-color-light] shadow-md" : ""}
          ${isPressed ? "scale-[0.9]" : "scale-100"}
        `}
      >
        <ProfilePicture
          profilePicture={accountDetails?.profilePicture}
          username={
            accountDetails?.firstName?.charAt(0) +
            accountDetails?.lastName?.charAt(0)
          }
        />
        {showCheckIcon && filterEnabled && (
          <div className="absolute bottom-0 right-0 bg-[var(--inyc-secondary)] rounded-kit-full p-kit-4 shadow-md">
            <FaCheck size={10} className="text-[--inyc-secondary-text-color]" />
          </div>
        )}
      </div>
      <div
        className="text-kit-md font-['Primary-Regular'] text-center text-[--inyc-primary-text-color] truncate"
        style={{ width: `calc(${size} + 2rem)` }}
      >
        {accountDetails?.businessName || accountDetails?.username}
      </div>
    </div>
  );
};

const WatchList = () => {
  const medium = useMediaQuery("md");
  const eventRef = useRef(null);
  const subEventRef = useRef(null);
  const searchInputRef = useRef();

  // Handle Enter key to dismiss keyboard on iOS
  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      // Blur the input to dismiss the keyboard
      if (searchInputRef.current) {
        searchInputRef.current.blur();
      }
    }
  };

  const {
    watchListEvents,
    watchListLoading,
    totalResults,
    setWatchListPage,
    pageLoading,
    refreshWatchList,
    seenArtists,
    queryFilter,
    setQueryFilter: setWatchListQueryFilter,
    dateFilter,
    setDateFilter: setWatchListDateFilter,
    tagFilter,
    setTagFilter: setWatchListTagFilter,
    accountFilter,
    setAccountFilter,
    toggleAccountFilter,
  } = useWatchList();
  const { mainContentRef } = useUIContext();
  const {
    selectEvent,
    selectSubEvent,
    setQueryFilter,
    setDateFilter,
    setTagFilter,
    selectedEvent,
    setSelectedEvent,
    selectedSubEvent,
    setSelectedSubEvent,
    setEventDetailsDateFilter,
  } = useEventDetails();
  const { user } = useUser();

  const { selectedAccount } = useAccountDetails();
  
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [watchlistTab, setWatchlistTab] = useState("artists");

  // Memoized onClick handler for EventCard to prevent unnecessary re-renders
  const handleEventCardClick = useCallback((event) => {
    // Pass WatchList search query to venue details if there's an active search
    if (queryFilter && queryFilter.trim() !== "") {
      setQueryFilter(queryFilter);
    } else {
      setQueryFilter("");
    }
    setDateFilter(null);
    setTagFilter([]);
    if (event.venueDetails?._id) {
      selectEvent(
        {
          ...event.venueDetails,
          type: "venue",
        },
        { ...event, _searchPageResult: true }
      );
    } else {
      selectEvent(event);
    }
  }, [queryFilter, setQueryFilter, setDateFilter, setTagFilter, selectEvent]);

  // Memoized onVenueClick handler for EventCard
  const handleVenueClick = useCallback((event) => {
    // Pass WatchList search query to venue details if there's an active search
    if (queryFilter && queryFilter.trim() !== "") {
      setQueryFilter(queryFilter);
    } else {
      setQueryFilter("");
    }
    setDateFilter(null);
    setTagFilter([]);
    if (event.venueDetails?._id) {
      selectEvent(
        {
          ...event.venueDetails,
          type: "venue",
        },
        { ...event, _searchPageResult: true }
      );
    } else {
      selectEvent(event);
    }
  }, [queryFilter, setQueryFilter, setDateFilter, setTagFilter, selectEvent]);

  // Filter out artists that the user is already following
  const seenArtistsFiltered = (seenArtists || []).filter(
    (artist) => !user?.following?.includes(artist._id)
  );

  console.log("mainContentRef ", mainContentRef?.current);

  return (
    <div className="overflow-x-hidden">
      <div className="max-w-screen-xl mx-auto p-kit-16">
        <div className={`${
            medium && (selectedEvent || selectedSubEvent || selectedAccount)
              ? "translate-x-[max(500px,35vw)]"
              : ""
          } duration-300 w-full md:max-w-[800px] md:mx-auto`}>
          <div className="text-kit-2xl mb-kit-8 flex items-center justify-between font-['Primary-Bold'] text-start">
            watchlist
          </div>
          <div className="text-kit-md font-['Primary-Regular'] text-start mb-kit-16 text-[var(--inyc-primary-text-color)] leading-[1.5]">
            quickly see upcoming shows from local artists you "watch"
            <br />
            tap the{" "}
            <LuEye
              className="inline-block mx-kit-4 align-text-bottom translate-y-[2px] md:translate-y-[1px]"
              size={22}
              color="var(--inyc-secondary)"
              aria-hidden="true"
            />{" "}
            on their profile, or a show they are tagged in
          </div>
          {user?.following?.length > 0 && (
            <div className={`md:max-w-[800px] mx-auto ${filtersExpanded ? "mb-kit-4 md:mb-0" : "mb-kit-6"}`}>
              <div className="relative flex items-center gap-kit-8">
                <Input
                  placeHolder="search events from artists you watch"
                  Ref={searchInputRef}
                  onChange={(e) => setWatchListQueryFilter(e.target.value)}
                  value={queryFilter}
                  icon={<FiSearch size={16} />}
                  onKeyDown={handleSearchKeyDown}
                  className={
                    "search-filters-input !mt-0 !mb-0 !mx-0"
                  }
                />
                {queryFilter && queryFilter.trim().length > 0 ? (
                  <button
                    className="absolute top-1/2 right-[3rem] transform -translate-y-1/2 p-kit-4 rounded-kit-full hover:bg-[var(--inyc-primary-light)] transition-colors duration-200 z-10"
                    onClick={() => setWatchListQueryFilter("")}
                    type="button"
                  >
                    <IoMdClose size={16} color="var(--inyc-primary-text-color-light)" />
                  </button>
                ) : (
                  <button
                    onClick={() => setFiltersExpanded(!filtersExpanded)}
                    className={`flex-shrink-0 cursor-pointer px-kit-6 py-kit-6 duration-100 rounded-kit-full border-[1px] border-[var(--inyc-input-border-color)] ${
                      filtersExpanded || tagFilter.length > 0 || dateFilter
                        ? "bg-[--inyc-secondary] text-[--inyc-secondary-text-color]"
                        : ""
                    }`}
                  >
                    <IoFilter size={16} />
                  </button>
                )}
              </div>
              <div
                className={`grid overflow-hidden duration-200 ${
                  filtersExpanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
                }`}
              >
                <div className="min-h-0">
                  <BottomSheetDateFilter
                    filterDate={dateFilter}
                    setFilterDate={setWatchListDateFilter}
                    tagFilter={tagFilter}
                    setTagFilter={setWatchListTagFilter}
                    className={"watchlist-date-filter !relative md:!min-w-full md:!w-full"}
                    hideFilterIcon={true}
                    alwaysShowTags={true}
                  />
                </div>
              </div>
            </div>
          )}
          <div className="mb-kit-16">
            <div className="flex items-baseline gap-kit-24">
              {[
                { id: "recent", label: "recent" },
                { id: "artists", label: "artists" },
              ].map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setWatchlistTab(tab.id)}
                  className={`py-kit-2 text-kit-lg font-['Primary-Medium'] leading-none lowercase tracking-[-0.035em] transition-colors duration-200 ${
                    watchlistTab === tab.id
                      ? "text-[var(--inyc-primary-text-color)]"
                      : "text-[var(--inyc-primary-text-color)] opacity-35 hover:opacity-65"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            <div className="mt-kit-12 border-b-[1px] border-[var(--inyc-input-border-color)]" />
          </div>
          {watchlistTab === "recent" && (
            seenArtistsFiltered.length > 0 ? (
              <div className="mb-kit-24 text-kit-md p-kit-16 flex gap-kit-16 overflow-x-auto overflow-y-hidden">
                {seenArtistsFiltered.map((artist) => (
                  <AccountBubble
                    key={artist._id}
                    account={artist}
                    filterEnabled={false}
                  />
                ))}
              </div>
            ) : (
              <div className="map-events-none !text-kit-md mt-kit-32">
                no recently viewed artists
              </div>
            )
          )}
          {watchlistTab === "artists" && (
            <>
              {user?.following?.length > 0 && (
                <>
                  {accountFilter.length > 0 && (
                    <div className="flex justify-end mb-kit-8">
                      <span
                        onClick={() => setAccountFilter([])}
                        className="text-kit-base text-[--inyc-secondary] cursor-pointer bg-[--inyc-primary-light] px-kit-12 py-kit-4 rounded-kit-lg"
                      >
                        clear filters
                      </span>
                    </div>
                  )}
                  <div className="w-full mb-kit-24 text-kit-md p-kit-16 flex gap-kit-16 overflow-x-auto overflow-y-hidden">
                    {user?.following?.map((artistId) => (
                      <AccountBubble
                        key={artistId}
                        account={{
                          _id: artistId,
                          username: "not available",
                          profilePicture: "not available",
                          businessName: "not available",
                        }}
                        isActive={
                          accountFilter.length === 0 ||
                          accountFilter.includes(artistId)
                        }
                        onFilter={toggleAccountFilter}
                        filterEnabled={true}
                        showCheckIcon={
                          accountFilter.length > 0 &&
                          accountFilter.includes(artistId)
                        }
                      />
                    ))}
                  </div>
                </>
              )}
              <div className="md:max-w-[800px] mx-auto">
                {watchListLoading ? (
                  <div className="flex justify-center items-center w-full h-[100px]">
                    <Loader />
                  </div>
                ) : !user?.following?.length ? (
                  <div className="map-events-none !text-kit-md mt-kit-32">
                    you're not watching any artist yet. <br /> <br />
                    watch artists by tapping the
                    <FaRegEye className="inline-block ml-kit-16 mr-kit-8" /> icon
                    on an artist posted event, or their profile.
                  </div>
                ) : !watchListEvents?.length ? (
                  <div className="map-events-none mt-kit-32 !text-kit-md font-['Primary-Regular'] !text-[var(--inyc-primary-text-color)]">
                    no upcoming events from artists you follow
                  </div>
                ) : (
                  <PullToActionWrapper
                    className={"pb-[5rem]"}
                    containerRef={mainContentRef}
                    onPull={refreshWatchList}
                  >
                    <InfiniteScroller
                      hasMore={watchListEvents.length < totalResults}
                      getData={() => {
                        setWatchListPage((prev) => ({
                          page: prev.page + 1,
                          limit: prev.limit,
                        }));
                      }}
                      loading={pageLoading}
                      items={watchListEvents}
                      renderer={(event, i) => (
                        <EventCard
                          key={i}
                          className="map-event-container"
                          fullWidth
                          latestEvent={event._matchingEvent}
                          data={event}
                          onClick={() => handleEventCardClick(event)}
                          onVenueClick={() => handleVenueClick(event)}
                          onEventClick={(ev) => {
                            selectSubEvent(ev);
                          }}
                        />
                      )}
                    />
                  </PullToActionWrapper>
                )}
              </div>
            </>
          )}
        </div>
        {medium && (
          <>
            {/* Event Details Panel */}
            <div
              ref={eventRef}
              className={`max-w-[35vw] min-w-[500px] ml-[--inyc-side-bar-width] watchlist-map-event-details-container duration-300 h-screen overflow-y-auto ${
                !selectedEvent ? "translate-x-[-100%]" : "translate-x-[0]"
              }`}
            >
              {selectedEvent && (
                <>
                  <EventDetails
                    className="!pt-kit-32"
                    parentRef={eventRef}
                    Close={() => {
                      setSelectedEvent(null);
                    }}
                    data={selectedEvent}
                    onSelectEvent={(event) => {
                      selectSubEvent(event);
                    }}
                  />
                </>
              )}
            </div>

            {/* Sub Event Details Panel */}
            <div
              ref={subEventRef}
              className={`max-w-[35vw] min-w-[500px] ml-[--inyc-side-bar-width] watchlist-map-sub-event-details-container duration-300 h-screen overflow-y-auto ${
                !selectedSubEvent ? "translate-x-[-100%]" : "translate-x-[0]"
              }`}
            >
              {selectedSubEvent && (
                <>
                  <EventDetails
                    className="!pt-kit-32"
                    parentRef={subEventRef}
                    Close={() => {
                      setSelectedSubEvent(null);
                      if (selectedEvent?.events?.length <= 1) {
                        setSelectedEvent(null);
                      }
                    }}
                    data={selectedSubEvent}
                  />
                </>
              )}
            </div>

            {/* Account Details Panel */}
            <div
              className={`max-w-[35vw] min-w-[500px] ml-[--inyc-side-bar-width] watchlist-map-account-details-container duration-300 h-screen overflow-y-auto ${
                !selectedAccount ? "translate-x-[-100%]" : "translate-x-[0]"
              }`}
            >
              {selectedAccount && <AccountDetails />}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default WatchList;
